import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Divider from '@mui/material/Divider';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchHotelModeConfigList, fetchHotelModeConfigInfo, addHotelModeConfig, fetchHotelModeExecutionHistory, fetchHotelModeExecutionInfo, deleteHotelModeConfig, executeHotelModeConfig } from '../services/api';
import { HotelModeConfigListItem, HotelModeAddConfigRequest, HotelModeAddConfigAppInfo, HotelModeExecutionHistoryRequest, HotelModeExecutionHistoryList, HotelModeExecutionInfoData } from '../types/hotelMode';

const getStatusText = (status: number): string => {
  switch (status) {
    case 0: return 'Idle';
    case 1: return 'Running';
    case 2: return 'Done';
    default: return 'Unknown';
  }
};

const getStatusColor = (status: number): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
  switch (status) {
    case 0: return 'default';  // Idle - gray
    case 1: return 'primary';  // Running - blue
    case 2: return 'success';  // Done - green
    default: return 'error';   // Unknown - red
  }
};

const getExecuteTypeText = (executeType: number): string => {
  switch (executeType) {
    case 0: return 'Manual';
    case 1: return 'Scheduled';
    default: return 'Unknown';
  }
};

const formatDate = (timestamp: number): string => {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleString();
};

const getDataHandlingText = (cleanData: number): string => {
  return cleanData === 0 ? 'Keep Data' : 'Clear Data';
};

const getDataHandlingColor = (cleanData: number): 'success' | 'warning' => {
  return cleanData === 0 ? 'success' : 'warning';
};

const getYesNoText = (value: number): string => {
  return value === 1 ? 'Yes' : 'No';
};

const getYesNoColor = (value: number): 'success' | 'error' => {
  const isYes = value === 1;
  return isYes ? 'success' : 'error';
};

const getExecutionMethodText = (execType: number): string => {
  switch (execType) {
    case 0: return 'Execute by SN';
    case 1: return 'Execute by Group';
    case 2: return 'Execute by CSV File';
    default: return 'Unknown';
  }
};

export default function AppDataPage() {
  // Tab state
  const [tabValue, setTabValue] = React.useState(0);
  
  // Manage Configurations state
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [pageInput, setPageInput] = React.useState('1');
  const [selectedConfigId, setSelectedConfigId] = React.useState<string | null>(null);
  const [viewModalOpen, setViewModalOpen] = React.useState(false);
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  
  // Execution History state
  const [historyPage, setHistoryPage] = React.useState(1);
  const [historyPageSize, setHistoryPageSize] = React.useState(20);
  const [historyPageInput, setHistoryPageInput] = React.useState('1');
  
  // Execution Info state
  const [selectedExecutionId, setSelectedExecutionId] = React.useState<string | null>(null);
  const [executionInfoModalOpen, setExecutionInfoModalOpen] = React.useState(false);
  
  // Filtered logs state
  const [filteredConfigId, setFilteredConfigId] = React.useState<string | null>(null);
  const [filteredConfigName, setFilteredConfigName] = React.useState<string | null>(null);
  const [isViewingFilteredLogs, setIsViewingFilteredLogs] = React.useState(false);
  
  // Delete confirmation state
  const [deleteConfigId, setDeleteConfigId] = React.useState<string | null>(null);
  const [deleteConfigName, setDeleteConfigName] = React.useState<string>('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  
  // Execute confirmation state
  const [executeConfigId, setExecuteConfigId] = React.useState<string | null>(null);
  const [executeConfigName, setExecuteConfigName] = React.useState<string>('');
  const [executeConfirmOpen, setExecuteConfirmOpen] = React.useState(false);
  const [addFormData, setAddFormData] = React.useState<HotelModeAddConfigRequest>({
    configName: '',
    description: '',
    appList: [],
    removeGuestApp: 0,
    restoreOriginalAccount: 0,
    execPlan: 0,
    execFrequency: '',
    execTypeExt: '',
    planTimePoint: 0,
    planStartTime: 0,
    planEndTime: 0
  });
  const [formErrors, setFormErrors] = React.useState<{[key: string]: string}>({});

  const queryClient = useQueryClient();

  const { data: response, isLoading, error, refetch } = useQuery({
    queryKey: ['hotelModeConfigList', page, pageSize],
    queryFn: () => fetchHotelModeConfigList({ page, pageSize }),
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Query for detailed config info when modal is open
  const { data: configInfoResponse, isLoading: configInfoLoading, error: configInfoError } = useQuery({
    queryKey: ['hotelModeConfigInfo', selectedConfigId],
    queryFn: () => fetchHotelModeConfigInfo(selectedConfigId!),
    enabled: !!selectedConfigId && viewModalOpen,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Query for execution history when execution history tab is active
  const { data: historyResponse, isLoading: historyLoading, error: historyError, refetch: refetchHistory } = useQuery({
    queryKey: ['hotelModeExecutionHistory', historyPage, historyPageSize, filteredConfigId],
    queryFn: () => {
      const request: HotelModeExecutionHistoryRequest = { 
        pageNum: historyPage, 
        pageSize: historyPageSize 
      };
      
      // Add configId filter if viewing filtered logs
      if (isViewingFilteredLogs && filteredConfigId) {
        request.entity = { configId: filteredConfigId };
      }
      
      return fetchHotelModeExecutionHistory(request);
    },
    enabled: tabValue === 1, // Only fetch when execution history tab is active
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Query for execution info when modal is open
  const { data: executionInfoResponse, isLoading: executionInfoLoading, error: executionInfoError } = useQuery({
    queryKey: ['hotelModeExecutionInfo', selectedExecutionId],
    queryFn: () => fetchHotelModeExecutionInfo(selectedExecutionId!),
    enabled: !!selectedExecutionId && executionInfoModalOpen,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Mutation for adding new hotel mode config
  const addConfigMutation = useMutation({
    mutationFn: addHotelModeConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hotelModeConfigList'] });
      setShowAddModal(false);
      resetAddForm();
    },
  });

  // Mutation for deleting hotel mode config
  const deleteConfigMutation = useMutation({
    mutationFn: deleteHotelModeConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hotelModeConfigList'] });
      setDeleteConfirmOpen(false);
      setDeleteConfigId(null);
      setDeleteConfigName('');
    },
  });

  // Mutation for executing hotel mode config
  const executeConfigMutation = useMutation({
    mutationFn: executeHotelModeConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hotelModeExecutionHistory'] });
      setExecuteConfirmOpen(false);
      setExecuteConfigId(null);
      setExecuteConfigName('');
    },
  });

  // Helper functions for add configuration form
  const resetAddForm = () => {
    setAddFormData({
      configName: '',
      description: '',
      appList: [],
      removeGuestApp: 0,
      restoreOriginalAccount: 0,
      execPlan: 0,
      execFrequency: '',
      execTypeExt: '',
      planTimePoint: 0,
      planStartTime: 0,
      planEndTime: 0
    });
    setActiveStep(0);
    setFormErrors({});
  };

  const validateStep1 = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!addFormData.configName.trim()) {
      errors.configName = 'Configuration name is required';
    }
    
    if (!addFormData.description.trim()) {
      errors.description = 'Description is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    addFormData.appList.forEach((app, index) => {
      if (!app.appName.trim()) {
        errors[`appName_${index}`] = 'App name is required';
      }
      if (!app.packageName.trim()) {
        errors[`packageName_${index}`] = 'Package name is required';
      } else if (!app.packageName.includes('.')) {
        errors[`packageName_${index}`] = 'Package name is invalid';
      }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const addApp = () => {
    setAddFormData(prev => ({
      ...prev,
      appList: [...prev.appList, { appName: '', packageName: '', cleanData: 0 }]
    }));
  };

  const removeApp = (index: number) => {
    setAddFormData(prev => ({
      ...prev,
      appList: prev.appList.filter((_, i) => i !== index)
    }));
  };

  const updateApp = (index: number, field: keyof HotelModeAddConfigAppInfo, value: string | number) => {
    setAddFormData(prev => ({
      ...prev,
      appList: prev.appList.map((app, i) => 
        i === index ? { ...app, [field]: value } : app
      )
    }));
  };

  const handleNext = () => {
    if (activeStep === 0 && validateStep1()) {
      setActiveStep(1);
    } else if (activeStep === 1 && validateStep2()) {
      // Submit form
      addConfigMutation.mutate(addFormData);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    setPageInput(String(newPage));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when page size changes
    setPageInput('1');
  };

  // Update pageInput when page changes
  React.useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  // Execution History helper functions
  const handleHistoryPageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setHistoryPage(newPage);
    setHistoryPageInput(String(newPage));
  };

  const handleHistoryPageSizeChange = (newPageSize: number) => {
    setHistoryPageSize(newPageSize);
    setHistoryPage(1);
    setHistoryPageInput('1');
  };

  React.useEffect(() => {
    setHistoryPageInput(String(historyPage));
  }, [historyPage]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Clear logs filter when manually switching tabs
    if (newValue === 1 && !isViewingFilteredLogs) {
      // If switching to execution history tab manually (not via logs button), clear any filter
      setFilteredConfigId(null);
      setIsViewingFilteredLogs(false);
    }
  };

  const formatExecuteTime = (timestamp: number): string => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  const handleViewConfig = (config: HotelModeConfigListItem) => {
    setSelectedConfigId(config.id);
    setViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setSelectedConfigId(null);
  };

  const handleViewExecution = (executionId: string) => {
    setSelectedExecutionId(executionId);
    setExecutionInfoModalOpen(true);
  };

  const handleCloseExecutionModal = () => {
    setExecutionInfoModalOpen(false);
    setSelectedExecutionId(null);
  };

  const handleViewLogs = (config: HotelModeConfigListItem) => {
    setFilteredConfigId(config.id);
    setFilteredConfigName(config.configName);
    setIsViewingFilteredLogs(true);
    setHistoryPage(1); // Reset to first page when filtering
    setHistoryPageInput('1');
    setTabValue(1); // Switch to execution history tab
  };

  const handleClearLogsFilter = () => {
    setFilteredConfigId(null);
    setFilteredConfigName(null);
    setIsViewingFilteredLogs(false);
    setHistoryPage(1);
    setHistoryPageInput('1');
  };

  const handleDeleteConfig = (config: HotelModeConfigListItem) => {
    setDeleteConfigId(config.id);
    setDeleteConfigName(config.configName);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteConfigId) {
      deleteConfigMutation.mutate(deleteConfigId);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setDeleteConfigId(null);
    setDeleteConfigName('');
  };

  const handleExecuteConfig = (config: HotelModeConfigListItem) => {
    setExecuteConfigId(config.id);
    setExecuteConfigName(config.configName);
    setExecuteConfirmOpen(true);
  };

  const handleConfirmExecute = () => {
    if (executeConfigId) {
      executeConfigMutation.mutate(executeConfigId);
    }
  };

  const handleCancelExecute = () => {
    setExecuteConfirmOpen(false);
    setExecuteConfigId(null);
    setExecuteConfigName('');
  };

  const configs = response?.data?.list || [];
  const totalPages = response?.data?.pages || 1;
  const totalItems = response?.data?.total || 0;

  // Execution History data
  const historyList = historyResponse?.data?.list || [];
  const historyTotalPages = historyResponse?.data?.pages || 1;
  const historyTotalItems = historyResponse?.data?.total || 0;

  if (error) {
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" className="page-title">Clear App Data</Typography>
            <Typography variant="body2" color="text.secondary">
              Clear App Data Configuration Management
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddModal(true)}
            sx={{ minWidth: '170px' }}
          >
            Add Configuration
          </Button>
        </Box>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }>
          Failed to load app data configurations: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" className="page-title">Clear App Data</Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="app data tabs">
          <Tab label="Manage Configurations" />
          <Tab label="Execution History" />
        </Tabs>
      </Box>

      {/* Add Configuration Button - Only shown for Manage Configurations tab */}
      {tabValue === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddModal(true)}
            sx={{ flexShrink: 0 }}
            disabled={true}
          >
            Add Configuration
          </Button>
        </Box>
      )}

      {/* Tab Content */}
      {tabValue === 0 && (
        <>
          <Paper sx={{ borderRadius: 2 }} elevation={2}>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Clear App Data Configurations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total configurations: {totalItems}
              </Typography>
            </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Configuration Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Execute Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Create Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configs.map((config: HotelModeConfigListItem) => (
                    <TableRow key={config.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {config.configName}
                        </Typography>
                        {config.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {config.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getStatusText(config.status)}
                          color={getStatusColor(config.status)}
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getExecuteTypeText(config.executeType)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {formatDate(config.createTime)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            size="small" 
                            variant="text" 
                            color="primary"
                            onClick={() => handleViewConfig(config)}
                          >
                            View
                          </Button>
                          <Button 
                            size="small" 
                            variant="text" 
                            color="primary"
                            disabled={true}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="small" 
                            variant="text" 
                            color="primary"
                            onClick={() => handleExecuteConfig(config)}
                          >
                            Execute
                          </Button>
                          <Button 
                            size="small" 
                            variant="text" 
                            color="primary"
                            onClick={() => handleViewLogs(config)}
                          >
                            Logs
                          </Button>
                          <Button 
                            size="small" 
                            variant="text" 
                            color="error"
                            onClick={() => handleDeleteConfig(config)}
                            sx={{ display: 'none' }} // Remove to unhide remove button
                          >
                            Remove
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {configs.length === 0 && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No app data configurations found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="configs-per-page-label">Per page</InputLabel>
                  <Select
                    labelId="configs-per-page-label"
                    value={pageSize}
                    label="Per page"
                    onChange={(e) => handlePageSizeChange(Number(e.target.value) || 20)}
                    MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff' } } }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                  Showing {configs.length} of {totalItems} configurations
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={handlePageChange} 
                  color="primary" 
                />
                <TextField
                  size="small"
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onBlur={() => {
                    const v = Number(pageInput) || 1;
                    const target = Math.max(1, Math.min(totalPages, Math.floor(v)));
                    if (target !== page) {
                      setPage(target);
                      setPageInput(String(target));
                    } else {
                      setPageInput(String(page));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const v = Number(pageInput) || 1;
                      const target = Math.max(1, Math.min(totalPages, Math.floor(v)));
                      setPage(target);
                      setPageInput(String(target));
                    }
                  }}
                  sx={{ width: 80 }}
                  placeholder="Page"
                />
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* View Configuration Modal */}
      <Dialog 
        open={viewModalOpen} 
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#ffffff',
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6">Configuration Details</Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 3}}>
          {configInfoLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : configInfoError ? (
            <Alert severity="error">
              Failed to load configuration details: {configInfoError instanceof Error ? configInfoError.message : 'Unknown error'}
            </Alert>
          ) : configInfoResponse?.data ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Basic Information */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Basic Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 2, alignItems: 'start' }}>
                  <Typography variant="body2" color="text.secondary">Name:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {configInfoResponse.data.configName}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">Description:</Typography>
                  <Typography variant="body2">
                    {configInfoResponse.data.description || '-'}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              {/* App Configurations */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  App Configurations
                </Typography>
                {configInfoResponse.data.appList && configInfoResponse.data.appList.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                          <TableCell sx={{ fontWeight: 600 }}>App Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Package Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Data Handling</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {configInfoResponse.data.appList.map((app, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{app.appName}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {app.packageName}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={getDataHandlingText(app.cleanData)}
                                color={getDataHandlingColor(app.cleanData)}
                                variant="filled"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No app configurations defined
                  </Typography>
                )}
              </Box>

              <Divider />

              {/* System Settings */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  System Settings
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2">
                    Uninstall apps that guests have downloaded from the Play Store (non-system):
                  </Typography>
                  <Chip
                    size="small"
                    label={getYesNoText(configInfoResponse.data.removeGuestApp)}
                    color={getYesNoColor(configInfoResponse.data.removeGuestApp)}
                    variant="filled"
                  />
                  
                  <Typography variant="body2">
                    Keep log-in account credentials (Gmail):
                  </Typography>
                  <Chip
                    size="small"
                    label={getYesNoText(configInfoResponse.data.restoreOriginalAccount)}
                    color={getYesNoColor(configInfoResponse.data.restoreOriginalAccount)}
                    variant="filled"
                  />
                </Box>
              </Box>

              <Divider />

              {/* Execution Method */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Execution Method
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2">
                    {getExecutionMethodText(configInfoResponse.data.execType)}
                  </Typography>
                  {configInfoResponse.data.execTypeExt && (
                    <>
                      <Typography variant="body2" color="text.secondary">-</Typography>
                      <Chip
                        size="small"
                        label={configInfoResponse.data.execTypeExt}
                        color="primary"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No configuration data available
            </Typography>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}

      {/* Execution History Tab */}
      {tabValue === 1 && (
        <>
          <Paper sx={{ borderRadius: 2 }} elevation={2}>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Execution History
                {isViewingFilteredLogs && (
                  <Chip
                    label="Filtered"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total execution histories: {historyTotalItems}
              </Typography>
            </Box>

            {/* Filter indicator and clear option */}
            {isViewingFilteredLogs && (
              <Box sx={{ px: 2, py: 1, backgroundColor: 'rgba(25, 118, 210, 0.08)', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="primary.main">
                    Showing logs for configuration: {filteredConfigName || filteredConfigId}
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleClearLogsFilter}
                    variant="outlined"
                    color="primary"
                  >
                    Show All Logs
                  </Button>
                </Box>
              </Box>
            )}

            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : historyError ? (
              <Box sx={{ p: 2 }}>
                <Alert severity="error" action={
                  <Button color="inherit" size="small" onClick={() => refetchHistory()}>
                    Retry
                  </Button>
                }>
                  Failed to load execution history: {historyError instanceof Error ? historyError.message : 'Unknown error'}
                </Alert>
              </Box>
            ) : historyList.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <Typography variant="body2" color="text.secondary">
                  No execution history found
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Configuration Name</TableCell>
                      <TableCell>Execute Time</TableCell>
                      <TableCell align="center">Total Devices</TableCell>
                      <TableCell align="center">Success</TableCell>
                      <TableCell align="center">Failed</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyList.map((history) => (
                      <TableRow key={history.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {history.configName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatExecuteTime(history.executeTime)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            {history.executeDeviceCount}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={history.successDeviceCount}
                            size="small"
                            color="success"
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={history.failedDeviceCount}
                            size="small"
                            color="warning"
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell align="center">
                            
                          <Button
                            size="small"
                            variant="text"
                            color="primary"
                            onClick={() => handleViewExecution(history.id)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Execution History Pagination */}
            {historyList.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 2, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="history-per-page-label">Per page</InputLabel>
                    <Select
                      labelId="history-per-page-label"
                      value={historyPageSize}
                      label="Per page"
                      onChange={(e) => handleHistoryPageSizeChange(Number(e.target.value) || 20)}
                      MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff' } } }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    Showing {historyList.length} of {historyTotalItems} execution histories
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Pagination 
                    count={historyTotalPages} 
                    page={historyPage} 
                    onChange={handleHistoryPageChange} 
                    color="primary" 
                  />
                  <TextField
                    size="small"
                    value={historyPageInput}
                    onChange={(e) => setHistoryPageInput(e.target.value)}
                    onBlur={() => {
                      const v = Number(historyPageInput) || 1;
                      const target = Math.max(1, Math.min(historyTotalPages, Math.floor(v)));
                      if (target !== historyPage) {
                        setHistoryPage(target);
                        setHistoryPageInput(String(target));
                      } else {
                        setHistoryPageInput(String(historyPage));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const v = Number(historyPageInput) || 1;
                        const target = Math.max(1, Math.min(historyTotalPages, Math.floor(v)));
                        setHistoryPage(target);
                        setHistoryPageInput(String(target));
                      }
                    }}
                    sx={{ width: 80 }}
                    placeholder="Page"
                  />
                </Box>
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* Execution Info Modal */}
      <Dialog 
        open={executionInfoModalOpen} 
        onClose={handleCloseExecutionModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'white'
          }
        }}
      >
        <DialogTitle sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Execution Details
          <IconButton onClick={handleCloseExecutionModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {executionInfoLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : executionInfoError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">
                Failed to load execution details: {executionInfoError instanceof Error ? executionInfoError.message : 'Unknown error'}
              </Alert>
            </Box>
          ) : executionInfoResponse?.data ? (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Basic Information */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Configuration Name</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {executionInfoResponse.data.configName}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Execute Time</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatExecuteTime(executionInfoResponse.data.executeTime)}
                      </Typography>
                    </Box>
                  </Box>
                  {executionInfoResponse.data.description && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">Description</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {executionInfoResponse.data.description}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider />

                {/* App Configuration */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>App Configuration</Typography>
                  {executionInfoResponse.data.appList && executionInfoResponse.data.appList.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: 'white' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>App Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Package Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Data Handling</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {executionInfoResponse.data.appList.map((app, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                {app.appName}
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                {app.packageName}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={app.cleanData === 0 ? 'Keep Data' : 'Clear Data'}
                                  size="small"
                                  color={app.cleanData === 0 ? 'success' : 'warning'}
                                  variant="filled"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No app configurations found
                    </Typography>
                  )}
                </Box>

                <Divider />

                {/* System Settings */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>System Settings</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Remove Guest App</Typography>
                      <Chip
                        label={executionInfoResponse.data.removeGuestApp === 1 ? 'Yes' : 'No'}
                        size="small"
                        color={executionInfoResponse.data.removeGuestApp === 1 ? 'success' : 'error'}
                        variant="filled"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Restore Original Account</Typography>
                      <Chip
                        label={executionInfoResponse.data.restoreOriginalAccount === 1 ? 'Yes' : 'No'}
                        size="small"
                        color={executionInfoResponse.data.restoreOriginalAccount === 1 ? 'success' : 'error'}
                        variant="filled"
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No execution data available
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseExecutionModal} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Configuration Modal */}
      <Dialog 
        open={showAddModal} 
        onClose={() => setShowAddModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#ffffff',
          },
        }}
      >
        <DialogTitle sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Add Clear App Data Configuration
          <IconButton onClick={() => setShowAddModal(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, py: 2 }}>
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              <Step>
                <StepLabel>Basic Information</StepLabel>
              </Step>
              <Step>
                <StepLabel>App Configuration</StepLabel>
              </Step>
            </Stepper>

            {activeStep === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Configuration Name"
                  value={addFormData.configName}
                  onChange={(e) => setAddFormData(prev => ({ ...prev, configName: e.target.value }))}
                  error={!!formErrors.configName}
                  helperText={formErrors.configName}
                  fullWidth
                  required
                />
                <TextField
                  label="Description"
                  value={addFormData.description}
                  onChange={(e) => setAddFormData(prev => ({ ...prev, description: e.target.value }))}
                  error={!!formErrors.description}
                  helperText={formErrors.description}
                  fullWidth
                  multiline
                  rows={3}
                  required
                />
              </Box>
            )}

            {activeStep === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* App List Section */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">App Configurations</Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addApp}
                      size="small"
                    >
                      Add App
                    </Button>
                  </Box>

                  {addFormData.appList.map((app, index) => (
                    <Paper key={index} sx={{ p: 2, mb: 2, backgroundColor: 'white' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle2">App {index + 1}</Typography>
                        <IconButton onClick={() => removeApp(index)} size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                          label="App Name"
                          value={app.appName}
                          onChange={(e) => updateApp(index, 'appName', e.target.value)}
                          error={!!formErrors[`appName_${index}`]}
                          helperText={formErrors[`appName_${index}`]}
                          fullWidth
                          required
                        />
                        <TextField
                          label="Package Name"
                          value={app.packageName}
                          onChange={(e) => updateApp(index, 'packageName', e.target.value)}
                          error={!!formErrors[`packageName_${index}`]}
                          helperText={formErrors[`packageName_${index}`] || ''}
                          fullWidth
                          required
                        />
                        <FormControl fullWidth>
                          <InputLabel>Data Handling</InputLabel>
                          <Select
                            value={app.cleanData}
                            onChange={(e) => updateApp(index, 'cleanData', e.target.value as number)}
                          >
                            <MenuItem value={0}>Keep Data</MenuItem>
                            <MenuItem value={1}>Clear Data</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Paper>
                  ))}
                </Box>

                <Divider />

                {/* System Settings Section */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>System Settings</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={addFormData.removeGuestApp === 1}
                          onChange={(e) => setAddFormData(prev => ({ 
                            ...prev, 
                            removeGuestApp: e.target.checked ? 1 : 0 
                          }))}
                        />
                      }
                      label="Remove Guest App"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={addFormData.restoreOriginalAccount === 1}
                          onChange={(e) => setAddFormData(prev => ({ 
                            ...prev, 
                            restoreOriginalAccount: e.target.checked ? 1 : 0 
                          }))}
                        />
                      }
                      label="Restore Original Account"
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={() => setShowAddModal(false)} 
            variant="outlined"
          >
            Cancel
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeStep === 1 && (
              <Button onClick={handleBack} variant="outlined">
                Back
              </Button>
            )}
            <Button 
              onClick={handleNext} 
              variant="contained"
              disabled={addConfigMutation.isPending}
            >
              {addConfigMutation.isPending && <CircularProgress size={20} sx={{ mr: 1 }} />}
              {activeStep === 0 ? 'Next' : 'Add Configuration'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#ffffff',
          },
        }}
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the configuration <strong>"{deleteConfigName}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelDelete} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            disabled={deleteConfigMutation.isPending}
          >
            {deleteConfigMutation.isPending && <CircularProgress size={20} sx={{ mr: 1 }} />}
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Execute Confirmation Dialog */}
      <Dialog 
        open={executeConfirmOpen} 
        onClose={handleCancelExecute}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#ffffff',
          },
        }}
      >
        <DialogTitle>
          Confirm Execute
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to execute the configuration <strong>"{executeConfigName}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelExecute} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmExecute} 
            variant="contained" 
            color="primary"
            disabled={executeConfigMutation.isPending}
          >
            {executeConfigMutation.isPending && <CircularProgress size={20} sx={{ mr: 1 }} />}
            Execute
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}